import React, { useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Upload, Loader2, ImageIcon, Film, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin media picker — handles BOTH images and videos.
 * - `kind`: "image" (default) | "video" | "any"
 * - Drag-drop / click to upload via /api/uploads
 * - URL paste fallback
 * - Inline preview for both kinds
 */
const MediaUpload = ({
  value = "",
  onChange,
  testId,
  label,
  kind = "image",
}) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const accept =
    kind === "video" ? "video/*" :
    kind === "any" ? "image/*,video/*" :
    "image/*";

  const resolveUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    if (url.startsWith("/api/")) {
      const base = API_BASE.replace(/\/api$/, "");
      return base + url;
    }
    return url;
  };

  const isVideoUrl = (url) => {
    if (!url) return false;
    const u = url.toLowerCase();
    return /\.(mp4|webm|mov|ogg|m4v)(\?|#|$)/.test(u);
  };

  const handleFile = async (file) => {
    if (!file) return;
    const okType =
      kind === "video" ? file.type.startsWith("video/") :
      kind === "any" ? (file.type.startsWith("image/") || file.type.startsWith("video/")) :
      file.type.startsWith("image/");
    if (!okType) {
      toast.error(`Please choose a ${kind === "video" ? "video" : kind === "any" ? "media" : "image"} file`);
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error("File must be 50MB or smaller");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/uploads", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(resolveUrl(res.data.url));
      toast.success(`${res.data.kind === "video" ? "Video" : "Image"} uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  const preview = resolveUrl(value);
  const showVideo = preview && isVideoUrl(preview);
  const Icon = kind === "video" ? Film : ImageIcon;
  const defaultLabel = kind === "video" ? "Video" : kind === "any" ? "Media" : "Image";

  return (
    <div data-testid={testId} className="space-y-2">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        className={`relative rounded-xl border-2 border-dashed transition cursor-pointer overflow-hidden ${
          dragOver ? "border-atc-blue bg-atc-blue/5" : "border-white/15 hover:border-white/30 bg-slate-950/50"
        }`}
      >
        <input
          ref={fileRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid={`${testId}-file-input`}
        />
        {preview ? (
          <div className="relative">
            {showVideo ? (
              <video src={preview} className="w-full max-h-64 object-cover bg-slate-950" controls preload="metadata" />
            ) : (
              <img src={preview} alt="" className="w-full max-h-64 object-cover" />
            )}
            <div className="absolute inset-0 bg-slate-950/60 opacity-0 hover:opacity-100 transition flex items-center justify-center gap-3">
              <span className="px-3 py-1.5 rounded-full bg-white/10 backdrop-blur text-white text-xs font-medium inline-flex items-center gap-1.5">
                <Upload className="h-3.5 w-3.5" /> Replace
              </span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onChange?.(""); }}
                className="px-3 py-1.5 rounded-full bg-rose-500/80 text-white text-xs font-medium inline-flex items-center gap-1.5"
                data-testid={`${testId}-clear`}
              >
                <X className="h-3.5 w-3.5" /> Remove
              </button>
            </div>
          </div>
        ) : (
          <div className="px-6 py-10 text-center">
            {uploading ? (
              <div className="flex flex-col items-center gap-2 text-slate-300">
                <Loader2 className="h-6 w-6 animate-spin" />
                <div className="text-sm">Uploading…</div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 text-slate-400">
                <Icon className="h-7 w-7" />
                <div className="text-sm font-medium text-slate-200">{label || defaultLabel}</div>
                <div className="text-xs">Drag &amp; drop or click to upload · max 50MB</div>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Input
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={`…or paste a ${defaultLabel.toLowerCase()} URL`}
          className="bg-slate-950 border-white/10 text-white text-xs"
          data-testid={`${testId}-url`}
        />
      </div>
    </div>
  );
};

export default MediaUpload;
